/**
 * Pagina amministrativa: import retroattivo dei file originali per documenti
 * anonimizzati vecchi, guidato da una tabella XLSX prodotta dall'anonimizzatore.
 *
 * Workflow:
 *   1. Upload XLSX di mapping (Nome Originale / Nome Anonimizzato)
 *      → backend risolve il Document.id per ogni riga.
 *   2. Drag-and-drop dei file binari originali (PDF). Il match con la tabella
 *      avviene client-side per filename (case-insensitive).
 *   3. Apply: i file delle righe `resolved` con file caricato vengono inviati
 *      al backend, salvati su disco, Document.original_file_path popolato.
 */
import React, { useState, useRef, useMemo } from 'react';
import {
  Upload, FileSpreadsheet, FileText, CheckCircle, AlertCircle, XCircle,
  Loader2, Trash2, Database
} from 'lucide-react';
import { api } from '../services/api';

const STATUS_META = {
  resolved: {
    label: 'Risolto',
    color: 'bg-green-100 text-green-800 border-green-300',
    Icon: CheckCircle,
  },
  resolved_overwrite: {
    label: 'Sovrascrivi',
    color: 'bg-orange-100 text-orange-800 border-orange-300',
    Icon: AlertCircle,
  },
  not_found: {
    label: 'Documento non trovato',
    color: 'bg-red-100 text-red-700 border-red-300',
    Icon: XCircle,
  },
  invalid: {
    label: 'Riga non valida',
    color: 'bg-red-100 text-red-700 border-red-300',
    Icon: XCircle,
  },
  duplicate_anonymized: {
    label: 'Duplicato nel mapping',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    Icon: AlertCircle,
  },
};

// Stati che, se hanno un file caricato, sono pronti per l'apply.
const APPLYABLE_STATUSES = new Set(['resolved', 'resolved_overwrite']);

const normalize = (s) => (s || '').trim().toLowerCase();

export default function BackfillOriginals({ onShowSuccess, onShowError }) {
  // Step 1: file XLSX caricato + righe parsate dal server
  const [xlsxFile, setXlsxFile] = useState(null);
  const [mappingRows, setMappingRows] = useState([]);
  const [mappingSummary, setMappingSummary] = useState(null);
  const [loadingParse, setLoadingParse] = useState(false);

  // Step 2: file binari trascinati dall'utente, indicizzati per filename normalizzato
  // Mappa: { 'franceschi egisto.pdf': File }
  const [filesByName, setFilesByName] = useState({});

  // Step 3: stato dell'apply
  const [loadingApply, setLoadingApply] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  // Progresso del batch durante l'apply (mostrato nella label del pulsante)
  // Forma: { current: number, total: number } | null
  const [applyProgress, setApplyProgress] = useState(null);

  // UX
  const [dragOverXlsx, setDragOverXlsx] = useState(false);
  const [dragOverFiles, setDragOverFiles] = useState(false);
  const xlsxInputRef = useRef(null);
  const filesInputRef = useRef(null);

  const showSuccess = (msg) => (onShowSuccess ? onShowSuccess(msg) : null);
  const showError = (msg) => (onShowError ? onShowError(msg) : alert(msg));

  // ---------------- Step 1: XLSX ----------------

  const handleXlsxSelect = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      showError('Atteso un file .xlsx');
      return;
    }
    setXlsxFile(file);
    setMappingRows([]);
    setMappingSummary(null);
    setApplyResult(null);
    setFilesByName({});

    setLoadingParse(true);
    try {
      const resp = await api.parseBackfillMapping(file);
      setMappingRows(resp.rows || []);
      setMappingSummary(resp.summary || null);
    } catch (err) {
      showError('Errore parsing XLSX: ' + err.message);
      setXlsxFile(null);
    } finally {
      setLoadingParse(false);
    }
  };

  const onXlsxDrop = (e) => {
    e.preventDefault();
    setDragOverXlsx(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleXlsxSelect(f);
  };

  const clearXlsx = () => {
    setXlsxFile(null);
    setMappingRows([]);
    setMappingSummary(null);
    setApplyResult(null);
    setFilesByName({});
    if (xlsxInputRef.current) xlsxInputRef.current.value = '';
  };

  // ---------------- Step 2: file binari ----------------

  const handleFilesSelect = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setFilesByName(prev => {
      const next = { ...prev };
      Array.from(fileList).forEach(f => {
        next[normalize(f.name)] = f;
      });
      return next;
    });
  };

  const onFilesDrop = (e) => {
    e.preventDefault();
    setDragOverFiles(false);
    handleFilesSelect(e.dataTransfer.files);
  };

  const clearFiles = () => {
    setFilesByName({});
    if (filesInputRef.current) filesInputRef.current.value = '';
  };

  // Genera le possibili chiavi di lookup per una riga del mapping.
  // Accetta sia il nome originale che il nome anonimizzato (con/senza .pdf).
  const lookupKeysForRow = (r) => {
    const keys = [];
    const orig = normalize(r.original_filename);
    const anon = normalize(r.anonymized_name);
    if (orig) keys.push(orig);
    if (anon) {
      keys.push(anon);
      if (!anon.endsWith('.pdf')) keys.push(anon + '.pdf');
    }
    return keys;
  };

  // Riga arricchita con `matched_file: File | null` (riferimento al binario uploadato).
  const enrichedRows = useMemo(() => {
    return mappingRows.map(r => {
      const keys = lookupKeysForRow(r);
      let matched = null;
      for (const k of keys) {
        if (filesByName[k]) { matched = filesByName[k]; break; }
      }
      return {
        ...r,
        matched_file: matched,
        file_uploaded: !!matched,
      };
    });
  }, [mappingRows, filesByName]);

  // File caricati ma non presenti nel mapping (filename → File)
  const unmatchedFiles = useMemo(() => {
    const wantedNames = new Set();
    mappingRows.forEach(r => {
      lookupKeysForRow(r).forEach(k => wantedNames.add(k));
    });
    return Object.values(filesByName).filter(f => !wantedNames.has(normalize(f.name)));
  }, [mappingRows, filesByName]);

  // Conteggi per il banner
  const counts = useMemo(() => {
    const c = { resolved: 0, overwrite: 0, not_found: 0, with_file: 0, ready_to_apply: 0 };
    enrichedRows.forEach(r => {
      if (r.status === 'resolved') c.resolved++;
      if (r.status === 'resolved_overwrite') c.overwrite++;
      if (r.status === 'not_found') c.not_found++;
      if (r.file_uploaded) c.with_file++;
      if (APPLYABLE_STATUSES.has(r.status) && r.file_uploaded) c.ready_to_apply++;
    });
    return c;
  }, [enrichedRows]);

  // ---------------- Step 3: apply ----------------

  // Soglie di chunking per evitare richieste oltre MAX_CONTENT_LENGTH (64MB backend).
  // Cap conservativo: ~50MB per chunk, max 25 file. Quello che scatta prima vince.
  const CHUNK_MAX_BYTES = 50 * 1024 * 1024;
  const CHUNK_MAX_FILES = 25;

  /**
   * Suddivide una lista di {file, mapEntry} in batch che rispettano i due cap.
   * Ritorna [{files: File[], mapping: Object[]}, ...].
   */
  const buildChunks = (items) => {
    const chunks = [];
    let currentFiles = [];
    let currentMapping = [];
    let currentSize = 0;

    for (const it of items) {
      const fSize = it.file.size || 0;
      const wouldExceedBytes = currentSize + fSize > CHUNK_MAX_BYTES;
      const wouldExceedCount = currentFiles.length + 1 > CHUNK_MAX_FILES;
      if (currentFiles.length > 0 && (wouldExceedBytes || wouldExceedCount)) {
        chunks.push({ files: currentFiles, mapping: currentMapping });
        currentFiles = [];
        currentMapping = [];
        currentSize = 0;
      }
      currentFiles.push(it.file);
      currentMapping.push(it.mapEntry);
      currentSize += fSize;
    }
    if (currentFiles.length > 0) {
      chunks.push({ files: currentFiles, mapping: currentMapping });
    }
    return chunks;
  };

  const runApply = async () => {
    const readyRows = enrichedRows.filter(r => APPLYABLE_STATUSES.has(r.status) && r.matched_file && r.document?.id);
    if (readyRows.length === 0) {
      showError('Nessuna riga pronta per essere applicata.');
      return;
    }

    // Costruisce coppie {file, mapEntry}, deduplica i file (un file può servire
    // più righe del mapping in casi limite, ma di solito 1 file = 1 documento).
    const items = [];
    const seenKeys = new Set();
    readyRows.forEach(r => {
      const f = r.matched_file;
      const key = normalize(f.name);
      if (seenKeys.has(key)) return;
      seenKeys.add(key);
      items.push({
        file: f,
        mapEntry: {
          filename: f.name,
          document_id: r.document.id,
          original_filename: r.original_filename,
        },
      });
    });

    const chunks = buildChunks(items);

    setLoadingApply(true);
    setApplyProgress({ current: 0, total: chunks.length });

    const aggregated = { applied: [], overwritten: [], errors: [] };

    try {
      for (let i = 0; i < chunks.length; i++) {
        setApplyProgress({ current: i + 1, total: chunks.length });
        try {
          const data = await api.applyBackfillMapping(chunks[i].files, chunks[i].mapping);
          if (data.applied) aggregated.applied.push(...data.applied);
          if (data.overwritten) aggregated.overwritten.push(...data.overwritten);
          if (data.errors) aggregated.errors.push(...data.errors);
        } catch (chunkErr) {
          // L'intero chunk è fallito: lo segnaliamo come errore aggregato.
          chunks[i].mapping.forEach(entry => {
            aggregated.errors.push({
              filename: entry.filename,
              document_id: entry.document_id,
              reason: `Batch ${i + 1}/${chunks.length} fallito: ${chunkErr.message}`,
            });
          });
        }
      }

      const finalResult = {
        applied: aggregated.applied,
        overwritten: aggregated.overwritten,
        errors: aggregated.errors,
        summary: {
          applied: aggregated.applied.length,
          overwritten: aggregated.overwritten.length,
          errors: aggregated.errors.length,
        },
      };
      setApplyResult(finalResult);
      showSuccess(
        `Import completato: ${finalResult.summary.applied} applicati, ` +
        `${finalResult.summary.overwritten} sovrascritti, ${finalResult.summary.errors} errori`
      );
    } finally {
      setLoadingApply(false);
      setApplyProgress(null);
    }
  };

  // ---------------- Render ----------------

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-800 flex items-center mb-2">
          <Database className="w-6 h-6 mr-2 text-blue-600" />
          Import file originali (mapping XLSX)
        </h2>
        <p className="text-sm text-gray-600">
          Carica il file <code className="bg-gray-100 px-1 rounded">Anonymizer_*.xlsx</code> con
          la mappatura tra <strong>Nome Originale</strong> e <strong>Nome Anonimizzato</strong>,
          poi trascina i file binari originali. Il sistema associa ogni file al documento
          corrispondente e popola <code>original_file_path</code>.
        </p>
      </div>

      {/* Step 1: XLSX */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center">
            <FileSpreadsheet className="w-5 h-5 mr-2 text-green-600" />
            1. Carica la tabella di mapping
          </h3>
          {xlsxFile && (
            <button
              onClick={clearXlsx}
              disabled={loadingParse || loadingApply}
              className="text-sm px-2 py-1 text-gray-600 hover:text-red-600 transition flex items-center"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Rimuovi
            </button>
          )}
        </div>

        {!xlsxFile ? (
          <div
            onDrop={onXlsxDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOverXlsx(true); }}
            onDragLeave={(e) => { e.preventDefault(); setDragOverXlsx(false); }}
            onClick={() => xlsxInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition ${
              dragOverXlsx ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-green-400 hover:bg-gray-50'
            }`}
          >
            <FileSpreadsheet className="w-8 h-8 mx-auto text-gray-400 mb-2" />
            <p className="text-gray-700 font-medium">Trascina qui il file XLSX o clicca per selezionarlo</p>
            <p className="text-xs text-gray-500 mt-1">Sheet atteso: "Documenti" con colonne Nome Originale / Nome Anonimizzato / Data</p>
            <input
              ref={xlsxInputRef}
              type="file"
              accept=".xlsx"
              onChange={(e) => handleXlsxSelect(e.target.files?.[0])}
              className="hidden"
            />
          </div>
        ) : (
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <FileSpreadsheet className="w-5 h-5 text-green-600 mr-2" />
                <span className="font-medium text-gray-800">{xlsxFile.name}</span>
                <span className="text-sm text-gray-500 ml-3">
                  {loadingParse ? 'Analisi…' : `${mappingRows.length} righe`}
                </span>
              </div>
              {loadingParse && <Loader2 className="w-5 h-5 animate-spin text-green-600" />}
            </div>
            {mappingSummary && !loadingParse && (
              <div className="flex flex-wrap gap-2 mt-3 text-xs">
                <span className="px-2 py-1 rounded-full bg-green-100 text-green-800">
                  ✓ {mappingSummary.resolved} risolti
                </span>
                {mappingSummary.resolved_overwrite > 0 && (
                  <span className="px-2 py-1 rounded-full bg-orange-100 text-orange-800">
                    ↻ {mappingSummary.resolved_overwrite} da sovrascrivere
                  </span>
                )}
                {mappingSummary.not_found > 0 && (
                  <span className="px-2 py-1 rounded-full bg-red-100 text-red-700">
                    ✗ {mappingSummary.not_found} non trovati
                  </span>
                )}
                {mappingSummary.duplicate > 0 && (
                  <span className="px-2 py-1 rounded-full bg-yellow-100 text-yellow-800">
                    ⚠ {mappingSummary.duplicate} duplicati
                  </span>
                )}
                {mappingSummary.invalid > 0 && (
                  <span className="px-2 py-1 rounded-full bg-red-100 text-red-700">
                    ✗ {mappingSummary.invalid} righe invalide
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Step 2: file binari */}
      {xlsxFile && !loadingParse && mappingRows.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <Upload className="w-5 h-5 mr-2 text-blue-600" />
              2. Carica i file binari originali
            </h3>
            {Object.keys(filesByName).length > 0 && (
              <button
                onClick={clearFiles}
                disabled={loadingApply}
                className="text-sm px-2 py-1 text-gray-600 hover:text-red-600 transition flex items-center"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Svuota
              </button>
            )}
          </div>

          <div
            onDrop={onFilesDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOverFiles(true); }}
            onDragLeave={(e) => { e.preventDefault(); setDragOverFiles(false); }}
            onClick={() => filesInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition ${
              dragOverFiles ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
            }`}
          >
            <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
            <p className="text-gray-700 font-medium">Trascina qui i file PDF originali o clicca per selezionarli</p>
            <p className="text-xs text-gray-500 mt-1">
              Il match avviene per "Nome Originale" (case-insensitive). Puoi caricare a tappe.
            </p>
            <input
              ref={filesInputRef}
              type="file"
              multiple
              onChange={(e) => handleFilesSelect(e.target.files)}
              className="hidden"
            />
          </div>

          {/* Banner counts */}
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-800">
              {Object.keys(filesByName).length} file caricati
            </span>
            <span className="px-2 py-1 rounded-full bg-green-100 text-green-800">
              {counts.ready_to_apply} pronti per l'import
            </span>
            {unmatchedFiles.length > 0 && (
              <span className="px-2 py-1 rounded-full bg-yellow-100 text-yellow-800">
                ⚠ {unmatchedFiles.length} file non riconosciuti dal mapping
              </span>
            )}
          </div>

          {unmatchedFiles.length > 0 && (
            <div className="mt-3 text-xs text-gray-600 bg-yellow-50 border border-yellow-200 rounded p-2">
              <div className="font-medium mb-1">File non presenti nel mapping (verranno ignorati):</div>
              <ul className="list-disc ml-5 max-h-24 overflow-y-auto">
                {unmatchedFiles.map((f, i) => (
                  <li key={i}>{f.name}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Tabella mapping */}
      {enrichedRows.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
            <FileText className="w-5 h-5 mr-2 text-gray-600" />
            Mappatura ({enrichedRows.length} righe)
          </h3>

          <div className="overflow-x-auto border border-gray-100 rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left bg-gray-50 border-b border-gray-200 text-gray-600">
                  <th className="py-2 px-3">#</th>
                  <th className="py-2 px-3">Nome Originale</th>
                  <th className="py-2 px-3">Nome Anonimizzato</th>
                  <th className="py-2 px-3">Documento</th>
                  <th className="py-2 px-3">Stato</th>
                  <th className="py-2 px-3">File caricato</th>
                </tr>
              </thead>
              <tbody>
                {enrichedRows.map((row) => {
                  const meta = STATUS_META[row.status] || STATUS_META.not_found;
                  const Icon = meta.Icon;
                  return (
                    <tr key={`${row.row_index}-${row.anonymized_name}`} className="border-b border-gray-100">
                      <td className="py-2 px-3 text-xs text-gray-500">{row.row_index}</td>
                      <td className="py-2 px-3 break-all">{row.original_filename}</td>
                      <td className="py-2 px-3">
                        <code className="text-xs bg-gray-100 px-1 rounded">{row.anonymized_name}</code>
                      </td>
                      <td className="py-2 px-3 text-xs text-gray-700">
                        {row.document ? (
                          <span>
                            #{row.document.id} · {row.document.name}
                            {row.document.username && <span className="text-gray-400"> · @{row.document.username}</span>}
                            {row.document.folder_path && row.document.folder_path !== '/' && (
                              <span className="text-gray-400"> · {row.document.folder_path}</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-gray-400 italic">—</span>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs ${meta.color}`}>
                          <Icon className="w-3 h-3 mr-1" />
                          {meta.label}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-xs">
                        {row.file_uploaded ? (
                          <span className="text-green-700 inline-flex items-center">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Caricato
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center justify-end gap-3">
            <span className="text-sm text-gray-600">
              {counts.ready_to_apply} su {counts.resolved} righe pronte
            </span>
            <button
              onClick={runApply}
              disabled={loadingApply || counts.ready_to_apply === 0}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition flex items-center"
            >
              {loadingApply ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {applyProgress
                    ? `Batch ${applyProgress.current}/${applyProgress.total}…`
                    : 'Applicazione…'}
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Applica import
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Summary risultato */}
      {applyResult && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">
            Risultato dell'import
          </h3>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="text-2xl font-bold text-green-700">{applyResult.summary?.applied || 0}</div>
              <div className="text-xs text-gray-600 mt-1">Applicati</div>
            </div>
            <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-200">
              <div className="text-2xl font-bold text-orange-700">{applyResult.summary?.overwritten || 0}</div>
              <div className="text-xs text-gray-600 mt-1">Sovrascritti</div>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg border border-red-200">
              <div className="text-2xl font-bold text-red-700">{applyResult.summary?.errors || 0}</div>
              <div className="text-xs text-gray-600 mt-1">Errori</div>
            </div>
          </div>

          {applyResult.errors?.length > 0 && (
            <div className="mt-3">
              <div className="font-medium text-sm text-red-700 mb-1">Errori:</div>
              <ul className="text-xs text-gray-600 list-disc ml-5 space-y-1">
                {applyResult.errors.map((e, i) => (
                  <li key={i}><strong>{e.filename}</strong>: {e.reason}</li>
                ))}
              </ul>
            </div>
          )}
          {applyResult.overwritten?.length > 0 && (
            <div className="mt-3">
              <div className="font-medium text-sm text-orange-700 mb-1">Sovrascritti:</div>
              <ul className="text-xs text-gray-600 list-disc ml-5 space-y-1">
                {applyResult.overwritten.map((e, i) => (
                  <li key={i}>
                    <strong>{e.filename}</strong> → doc #{e.document_id} ({e.document_name})
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
